/**
 * Wave 2 — Spec §7.4 fee-before-submission disclosure.
 *
 * "A $X.XX service fee will be added for credit card payments. Total: $Y.YY."
 * Conditional on the tenant's surcharge setting; defaults to off.
 */
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  effectivePaymentMethodDiscountPercent,
  formatDiscountPercent,
} from '@/lib/payments/paymentMethodDiscount';

export interface SurchargeDisclosureProps {
  /** Pre-surcharge amount the customer is charging. */
  baseAmount: number;
  /** The surcharge they will pay (computed from tenant settings + method). */
  surchargeAmount: number;
  /** Whether the disclosure is enabled (read from tenant settings). */
  enabled: boolean;
  /** Whether ACH/check can actually be selected on this payment surface. */
  achOrCheckOffered?: boolean;
  /** Currency formatter override; defaults to USD. */
  currency?: string;
  className?: string;
}

function formatMoney(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function SurchargeDisclosure({
  baseAmount,
  surchargeAmount,
  enabled,
  achOrCheckOffered = true,
  currency = 'USD',
  className,
}: SurchargeDisclosureProps) {
  if (!enabled || surchargeAmount <= 0) return null;
  const cardPrice = baseAmount + surchargeAmount;
  const discountPercent = effectivePaymentMethodDiscountPercent(baseAmount, surchargeAmount);
  if (!achOrCheckOffered) {
    return (
      <div
        className={cn(
          'flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700',
          className,
        )}
        data-testid="surcharge-disclosure"
      >
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>
          Card price: {formatMoney(cardPrice, currency)}. ACH/check price:{' '}
          {formatMoney(baseAmount, currency)} (not available online).
        </span>
      </div>
    );
  }
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900',
        className,
      )}
      data-testid="surcharge-disclosure"
    >
      <Info size={14} className="mt-0.5 shrink-0" />
      <div>
        <span className="font-medium">
          Pay by ACH or check and save {formatMoney(surchargeAmount, currency)} (
          {formatDiscountPercent(discountPercent)}).
        </span>{' '}
        <span className="opacity-80">
          Card price: {formatMoney(cardPrice, currency)}; ACH/check price:{' '}
          {formatMoney(baseAmount, currency)}.
        </span>
      </div>
    </div>
  );
}
